import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ChevronLeft,
  Download,
  Star,
  Share2,
  Bookmark,
  Clock,
  FileText,
  Users,
  Eye,
} from 'lucide-react';
import { Resource } from '@/types/library';
import { toast } from 'sonner';

interface ResourceViewerProps {
  resource: Resource;
  onBack: () => void;
}

export default function ResourceViewer({ resource, onBack }: ResourceViewerProps) {
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [userRating, setUserRating] = useState(0);

  const handleDownload = () => {
    toast.success(`${resource.title} downloaded successfully!`);
  };

  const handleShare = () => {
    toast.success('Share link copied to clipboard!');
  };

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
    toast.success(isBookmarked ? 'Removed from bookmarks' : 'Added to bookmarks');
  };

  const handleRate = (rating: number) => {
    setUserRating(rating);
    toast.success(`You rated this resource ${rating} stars`);
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      textbook: '📕',
      guide: '📗',
      notes: '📝',
      worksheet: '📄',
      'past-paper': '📋',
      reference: '📕',
    };
    return icons[type] || '📄';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border sticky top-0 z-40 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button onClick={onBack} variant="ghost" size="sm">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-primary truncate">{resource.title}</h1>
              <p className="text-sm text-muted-foreground">{resource.subject}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Preview Area */}
            <Card className="border-border aspect-video bg-gradient-to-br from-primary/20 to-secondary/20 rounded-lg flex items-center justify-center overflow-hidden">
              <div className="text-center space-y-4">
                <div className="text-8xl">{getTypeIcon(resource.type)}</div>
                <div className="space-y-2">
                  <p className="text-lg font-semibold text-primary">Document Preview</p>
                  <p className="text-sm text-muted-foreground">
                    Download to view the full content
                  </p>
                </div>
              </div>
            </Card>

            {/* Resource Info */}
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-primary">{resource.title}</h2>
                    {resource.author && (
                      <p className="text-muted-foreground">by {resource.author}</p>
                    )}
                  </div>
                  <Badge className="bg-blue-100 text-blue-800">
                    {resource.type.replace('-', ' ')}
                  </Badge>
                </div>

                {resource.description && (
                  <p className="text-muted-foreground leading-relaxed mt-4">
                    {resource.description}
                  </p>
                )}
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-4 pt-6 pb-6 border-t border-b border-border">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{resource.downloads}</div>
                  <p className="text-xs text-muted-foreground mt-1">Downloads</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Star className="w-5 h-5 fill-accent text-accent" />
                    <span className="text-2xl font-bold text-primary">{resource.rating}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Rating</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{resource.reviews}</div>
                  <p className="text-xs text-muted-foreground mt-1">Reviews</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{resource.pages || '-'}</div>
                  <p className="text-xs text-muted-foreground mt-1">Pages</p>
                </div>
              </div>

              {/* Metadata */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <FileText className="w-4 h-4" />
                    File Size
                  </span>
                  <span className="font-semibold text-foreground">{resource.fileSize} MB</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    Uploaded
                  </span>
                  <span className="font-semibold text-foreground">
                    {resource.uploadedAt.toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* Tags */}
              {resource.tags.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {resource.tags.map((tag) => (
                      <Badge key={tag} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Tabs */}
            <Card className="border-border">
              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-2 border-b border-border rounded-none bg-muted/50">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="reviews">Reviews ({resource.reviews})</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="p-6 space-y-4">
                  <h3 className="font-semibold text-foreground">About this resource</h3>
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <p>
                      This {resource.type} is a comprehensive study material covering key concepts
                      and topics for {resource.class} students. It's designed to complement classroom
                      learning and help students prepare effectively.
                    </p>
                    <p>
                      The resource includes detailed explanations, worked examples, and practice
                      problems to reinforce understanding. It has been highly rated by students and
                      educators alike.
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="reviews" className="p-6 space-y-4">
                  {/* User Rating */}
                  <div className="p-4 bg-primary/5 rounded-lg border border-primary/20 space-y-3">
                    <p className="font-medium text-foreground">Rate this resource</p>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          onClick={() => handleRate(rating)}
                          className="transition-transform hover:scale-110"
                        >
                          <Star
                            className={`w-6 h-6 ${
                              rating <= userRating
                                ? 'fill-accent text-accent'
                                : 'text-muted-foreground'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Sample Reviews */}
                  <div className="space-y-4 pt-4 border-t border-border">
                    {[
                      {
                        name: 'Chioma O.',
                        rating: 5,
                        date: '2 weeks ago',
                        text: 'Excellent resource! Very comprehensive and well-organized. Helped me ace my exams!',
                      },
                      {
                        name: 'Tunde A.',
                        rating: 4,
                        date: '1 month ago',
                        text: 'Great content but could use more practice problems. Overall very helpful.',
                      },
                    ].map((review, index) => (
                      <div key={index} className="p-4 border border-border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-foreground">{review.name}</span>
                          <span className="text-xs text-muted-foreground">{review.date}</span>
                        </div>
                        <div className="flex gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < review.rating
                                  ? 'fill-accent text-accent'
                                  : 'text-muted-foreground'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-sm text-muted-foreground">{review.text}</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </Card>
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            {/* Download Card */}
            <Card className="p-6 border-border space-y-4">
              <Button
                onClick={handleDownload}
                className="w-full bg-primary hover:bg-primary/90 gap-2 h-12"
              >
                <Download className="w-5 h-5" />
                Download Now
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                {resource.fileSize} MB • {resource.pages} pages
              </p>

              <div className="border-t border-border pt-4 space-y-3">
                <Button
                  onClick={handleShare}
                  variant="outline"
                  className="w-full gap-2 border-border"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
                <Button
                  onClick={handleBookmark}
                  variant={isBookmarked ? 'default' : 'outline'}
                  className={`w-full gap-2 ${
                    isBookmarked
                      ? 'bg-secondary hover:bg-secondary/90'
                      : 'border-border'
                  }`}
                >
                  <Bookmark className={`w-4 h-4 ${isBookmarked ? 'fill-current' : ''}`} />
                  {isBookmarked ? 'Bookmarked' : 'Bookmark'}
                </Button>
              </div>
            </Card>

            {/* Info Card */}
            <Card className="p-6 border-border space-y-4">
              <h3 className="font-semibold text-foreground">Resource Info</h3>

              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <FileText className="w-4 h-4" />
                  <span>{resource.type.replace('-', ' ')}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Eye className="w-4 h-4" />
                  <span>{resource.downloads} downloads</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="w-4 h-4" />
                  <span>{resource.reviews} reviews</span>
                </div>
              </div>
            </Card>

            {/* Class & Subject */}
            <Card className="p-6 border-border space-y-3">
              <h3 className="font-semibold text-foreground">Class & Subject</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Class</span>
                  <Badge variant="outline">{resource.class}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Subject</span>
                  <Badge variant="outline">{resource.subject}</Badge>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
